"use client";

import { createContext, useCallback, useContext, useEffect, useState } from "react";

interface DiscreetModeContextValue {
  discreet: boolean;
  toggle: () => void;
  setDiscreet: (next: boolean) => void;
}

const DiscreetModeContext = createContext<DiscreetModeContextValue | null>(null);

const STORAGE_KEY = "dlist_discreet_mode";

/**
 * Discreet Mode — full-app grayscale toggle for public-place use.
 *
 * Per CLAUDE.md "plausibly deniable on a glance" and the "wife-glance
 * disaster" anti-pattern: in a coffee shop / on the bus / at work,
 * a colorful row of body shots reads as "cruising/dating app" to
 * any bystander glancing over. Discreet Mode strips ALL color via a
 * single CSS filter on the html root — photos go grayscale, accent
 * green flattens, the whole app reads like a notes app or terminal
 * at a glance. The user can still navigate normally; passersby
 * can't pattern-match the cruising context.
 *
 * Implementation: a single CSS rule (html.discreet { filter:
 * grayscale(1) }) applied to the document element. Toggle persists
 * in localStorage so the preference survives page reloads. Provider
 * sits at the root layout so any client component can read the
 * state or trigger the toggle.
 *
 * Toggle UI lives at the top of /me — see DiscreetModeToggle.
 *
 * Edge cases worth knowing:
 *   - Maps/iOS share sheet/push notifications are OS-rendered and
 *     stay in normal color. Discreet Mode is in-app only.
 *   - The CSS filter applies to ALL descendants including images.
 *     This is intentional — photos are the bigger "cruising app"
 *     tell than the accent green.
 */
export function DiscreetModeProvider({
  children,
}: {
  children: React.ReactNode;
}) {
  // Initial state: read from localStorage on mount. Default false
  // (color is the standard experience). SSR returns false; the
  // useEffect below picks up the persisted value on hydration.
  const [discreet, setDiscreetState] = useState(false);

  // Load persisted preference on mount.
  useEffect(() => {
    try {
      const raw = window.localStorage.getItem(STORAGE_KEY);
      if (raw === "true") setDiscreetState(true);
    } catch {
      // localStorage unavailable (Safari private mode etc.) — silent.
    }
  }, []);

  // Apply / remove CSS class on the html element + persist on change.
  useEffect(() => {
    const root = document.documentElement;
    if (discreet) root.classList.add("discreet");
    else root.classList.remove("discreet");
    try {
      window.localStorage.setItem(STORAGE_KEY, String(discreet));
    } catch {
      // ignore
    }
  }, [discreet]);

  const setDiscreet = useCallback((next: boolean) => {
    setDiscreetState(next);
  }, []);

  const toggle = useCallback(() => {
    setDiscreetState((prev) => !prev);
  }, []);

  return (
    <DiscreetModeContext.Provider value={{ discreet, toggle, setDiscreet }}>
      {children}
    </DiscreetModeContext.Provider>
  );
}

export function useDiscreetMode(): DiscreetModeContextValue {
  const ctx = useContext(DiscreetModeContext);
  if (!ctx) {
    throw new Error("useDiscreetMode must be used inside DiscreetModeProvider");
  }
  return ctx;
}
